import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { vbaSprache } from "@/office/makro/sprache";
import { MAKRO_SPRACHE } from "../makros";

export const lektionSprache: Lesson = {
  id: "sprache-rechtschreibung",
  title: "Sprache und Rechtschreibung",
  summary: "Warum PowerPoint ganze Folien rot unterringelt – und wie man es behebt.",
  durationMinutes: 8,
  learningGoals: [
    "Die Sprache von Text festlegen",
    "Die Rechtschreibprüfung vor dem Vortrag durchlaufen lassen",
  ],
  steps: [
    {
      id: "sprache",
      title: "Die Sprache festlegen",
      explanation: [
        "Wie in Word ist die Sprache eine Eigenschaft des Textes, nicht der Datei. Folien aus " +
          "einer englischen Vorlage oder von Kolleginnen mit englischem Office prüft " +
          "PowerPoint gegen das englische Wörterbuch – jedes deutsche Wort ist dann rot.",
        "„Überprüfen“ → „Sprache“ → „Sprache für die Korrekturhilfen festlegen“ ändert die " +
          "Sprache des markierten Textes. Markiert man vorher in der Miniaturleiste alle " +
          "Folien, gilt das nicht – die Sprache muss am Text gesetzt werden. Schneller geht " +
          "es in der Gliederungsansicht: Strg + A, dann die Sprache wählen.",
        "Die Sprache steuert auch Screenreader: Ein deutscher Text mit englischer Kennung " +
          "wird mit englischer Aussprache vorgelesen.",
      ],
      keyPoints: [
        "Überprüfen → Sprache → Sprache für die Korrekturhilfen festlegen.",
        "Gliederungsansicht, Strg + A, dann die Sprache für alles setzen.",
        "Die Sprache steuert auch die Aussprache von Screenreadern.",
      ],
      task: {
        instruction: "Sorge dafür, dass der Text auf allen Folien als Deutsch gekennzeichnet ist.",
        hint: "Ansicht → Gliederungsansicht, links in den Text klicken, Strg + A, Überprüfen → Sprache → Deutsch.",
        macro: MAKRO_SPRACHE,
        check: () =>
          pruefeMitMakro(MAKRO_SPRACHE, (bericht) => {
            const sprachen = [...bericht.gruppe("sprache").values()].map((eintrag) => ({
              name: vbaSprache(Number(eintrag.get("id"))),
              formen: Number(eintrag.get("formen") ?? 0),
            }));
            const fremd = sprachen.filter((sprache) => sprache.name !== "German");
            return fromCriteria(
              [
                criterion("Die Präsentation enthält Text", sprachen.length > 0),
                criterion(
                  fremd.length === 0
                    ? "Aller Text ist als Deutsch gekennzeichnet"
                    : `Abweichend: ${fremd.map((s) => `${s.name} (${s.formen} Formen)`).join(", ")}`,
                  sprachen.length > 0 && fremd.length === 0,
                ),
              ],
              {
                success: "Die Rechtschreibprüfung arbeitet jetzt mit dem richtigen Wörterbuch.",
                failure: "Noch ist nicht aller Text als Deutsch gekennzeichnet.",
                hint: "Formen mit gemischter Sprache zählen als abweichend – den ganzen Text markieren.",
              },
            );
          }),
      },
    },
    {
      id: "rechtschreibung",
      title: "Die Prüfung vor dem Vortrag",
      explanation: [
        "F7 startet die Rechtschreibprüfung für die ganze Präsentation – auch für Notizen und " +
          "Texte, die im Bearbeitungsfenster gerade nicht zu sehen sind. Ein Tippfehler auf " +
          "dem Beamer ist größer als in jedem Dokument.",
        "Die Prüfung findet keine Wörter, die es gibt, aber nicht an diese Stelle gehören. " +
          "Deshalb vor jedem Vortrag einmal die Folien in der Leseansicht durchgehen – laut, " +
          "wenn es geht.",
      ],
      keyPoints: ["F7 prüft alle Folien und Notizen.", "Danach selbst in der Leseansicht gegenlesen."],
    },
  ],
};
